/**
 * TrendingAllSheet — 요즘 많이 모으는 카드 · 전체 (PKA 화면정의서 SC-019a).
 *
 * 홈 §2 의 '더보기'. **페이지 전환 없이 바텀시트로** 연다 —
 * 홈은 진입 첫 탭이라 스크롤 위치를 잃지 않는 게 중요하고, 목록은 홈 §2 의
 * 연장선이지 별도 목적지가 아니다. (design 결정 — 종전 /trending 라우트 대체)
 *
 * 홈에서 고른 필터 칩을 **승계**한다. 뷰는 v1.0 심플 하나뿐 — 목록·분할·차트 뷰는
 * v2.0 이월(IA SC-019a).
 *
 * ⚠ 무한 스크롤이 아니라 **고정 상한 100위** 이다 (SC-019a · FN-048, 2026-08-28
 * 전수조사 확정 — 종전 50). 트렌딩은 "지금 인기 순위"라 유계
 * 목록이 자연스럽고 BE 랭킹 쿼리도 페이지네이션이 없다(TrendChipKey — 시세 기반
 * v1 근사치). design 원본의 더미 순환 페이지네이션은 서버 연동으로 대체됐다.
 */
import { useEffect, useState } from "react";
import { useMoney } from "../../hooks/useMoney";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import { useTranslation } from "react-i18next";
import { Button } from "@astryxdesign/core/Button";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Item } from "@astryxdesign/core/Item";
import { List } from "@astryxdesign/core/List";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { ToggleButton } from "@astryxdesign/core/ToggleButton";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import Sheet from "../feedback/Sheet";
import TrendChip from "../card/TrendChip";
import { useEdition } from "../edition/EditionContext";
import { cardImageUrl, type CardItem, type TrendChipKey } from "../../api/card";
import { useTrendingQuery } from "../../hooks/useCardQueries";
import { useAuthStore } from "../../store/authStore";
import { maskPrice } from "../../lib/priceMasking";
import CardThumb from "../card/CardThumb";
import SwipeTabPanel from "../primitives/SwipeTabPanel";

/** SC-019a 확장 목록 상한 — 칩당 100위. BE 트렌딩 최대치(TREND_MAX_LIMIT)와 같은 값이다. */
const FULL_LIMIT = 100;

interface TrendingAllSheetProps {
  open: boolean;
  onClose: () => void;
  /** 홈 §2 에서 고른 칩 — 시트를 열 때 그대로 이어받는다. */
  initialChip: TrendChipKey;
  /** 홈과 같은 칩 목록을 쓴다 (표시 순서 공유). */
  chips: TrendChipKey[];
}

export default function TrendingAllSheet({ open, onClose, initialChip, chips }: TrendingAllSheetProps) {
  const { t } = useTranslation("card");
  const money = useMoney();
  const overlayNavigate = useOverlayNavigate();
  const { edition } = useEdition();
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const [chip, setChip] = useState<TrendChipKey>(initialChip);

  /** 열 때마다 홈의 현재 칩을 이어받는다. */
  useEffect(() => {
    if (open) setChip(initialChip);
  }, [open, initialChip]);

  // 닫혀 있을 때는 부르지 않는다 — 홈 §2(5건)와 key 가 달라 별도 쿼리다.
  const trending = useTrendingQuery(chip, edition, FULL_LIMIT, { enabled: open });

  const priceText = (card: CardItem): string | null => {
    const price = card.listPrice;
    if (price.display === "NONE" || price.amount === null) return null;
    return isAuthenticated ? money.format(price.amount, price.currency ?? undefined) : maskPrice(price.amount);
  };

  return (
    <Sheet open={open} onClose={onClose} ariaLabel={t("home.trendingTitle")} variant="tall" backKind="trending-all-sheet">
      <VStack gap={4} padding={5}>
        <Heading level={3}>{t("home.trendingTitle")}</Heading>

        {/* 필터 칩 승계 — 홈 §2 와 같은 3종 */}
        <HStack gap={1} wrap="wrap">
          {chips.map((c) => (
            <ToggleButton
              key={c}
              size="sm"
              data-compact=""
              label={t(`home.trendChip.${c.toLowerCase()}`)}
              isPressed={chip === c}
              onPressedChange={() => setChip(c)}
            />
          ))}
        </HStack>

        <SwipeTabPanel tabs={chips} activeTab={chip} onTabChange={setChip}>
        {trending.isPending ? (
          <VStack gap={2} aria-hidden="true">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={56} />)}
          </VStack>
        ) : trending.isError ? (
          <EmptyState
            title={t("search.loadFailed")}
            description={t("search.loadFailedBody")}
            actions={
              <Button
                variant="secondary"
                label={t("search.retry")}
                clickAction={() => void trending.refetch()}
              />
            }
          />
        ) : trending.data.length === 0 ? (
          <EmptyState title={t("home.trendEmpty")} description={t("home.trendEmptyBody")} />
        ) : (
          <>
            {/* 인피드 광고 — 9번째부터 10개마다 (수익모델 §3.1).
                이 목록이 광고의 주 무대다: 무한 스크롤이고, 발견 존이라
                내 자산·시세 옆이 아니다. */}
            <List hasDividers>
              {trending.data.map((card) => {
                const price = priceText(card);
                return (
                  <Item
                    key={card.cardSerialNo}
                    as="li"
                    label={card.name}
                    description={[card.cardsetCode.toUpperCase(), card.number].filter(Boolean).join(" ")}
                    labelLines={1}
                    onClick={() => { onClose(); overlayNavigate(`/cards/${card.cardSerialNo}`); }}
                    startContent={<CardThumb imgUrl={cardImageUrl(card.imagePathway) ?? undefined} />}
                    endContent={
                      <VStack gap={0.5} align="end">
                        <Text type="body" weight="bold">{price ?? t("search.noPrice")}</Text>
                        <TrendChip value={card.listPrice.trendRate} />
                      </VStack>
                    }
                  />
                );
              })}
            </List>
            <Text type="supporting" color="secondary" justify="center">{t("home.listEnd")}</Text>
          </>
        )}
        </SwipeTabPanel>
      </VStack>
    </Sheet>
  );
}
